'use client';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { Button } from '@shared/components/ui/button';
import { DateRangePicker } from '@shared/components/ui/DateRangePicker';
import { MultiSelect } from '@shared/components/ui/MultiSelect';
import { cn } from '@shared/utils/utils';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PreparationType } from '@modules/OrderPreparation/shared/config';
import { useCartsSimple } from '@modules/Carts/hooks';
import { useOrderPreparationFilters } from '../../hooks/useOrderPreparationFilters';

interface OrderPreparationFiltersPanelProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
  className?: string;
}

export const OrderPreparationFiltersPanel = ({
  initialConditions = [],
  onChange,
  className,
}: OrderPreparationFiltersPanelProps) => {
  const { t } = useTranslation('orderpreparationModule');

  const {
    filters,
    handleStatusChange,
    handleTypeChange,
    handleLocationIdsChange,
    handleCourierChange,
    handleCreatedAtChange,
    handleQtyChange,
    handleQtyDoneChange,
    clearAllFilters,
    courierOptions,
    isLoadingCouriers,
  } = useOrderPreparationFilters({ initialConditions, onChange });
  const { carts } = useCartsSimple({ limit: 100 });

  const statusOptions = useMemo(
    () => [
      { value: '0', label: t('filters.options.status.0') },
      { value: '1', label: t('filters.options.status.1') },
      { value: '2', label: t('filters.options.status.2') },
      { value: '3', label: t('filters.options.status.3') },
    ],
    [t],
  );

  const typeOptions = useMemo(
    () => [
      { value: String(PreparationType.PICKING), label: t('filters.options.type.1') },
      {
        value: String(PreparationType.MASSIVE_PACKING),
        label: t('filters.options.type.3'),
      },
    ],
    [t],
  );

  const [localQty, setLocalQty] = useState(filters.qty);
  const [localQtyDone, setLocalQtyDone] = useState(filters.qty_done);
  const cartOptions = useMemo(
    () =>
      carts.map((cart) => ({
        value: String(cart.id),
        label: cart.code,
      })),
    [carts],
  );

  useEffect(() => {
    setLocalQty(filters.qty);
    setLocalQtyDone(filters.qty_done);
  }, [filters.qty, filters.qty_done]);

  const applyQty = (type: 'min' | 'max', value: string) => {
    if (type === 'min') handleQtyChange('min', value);
    else handleQtyChange('max', value);
  };
  const applyQtyDone = (type: 'min' | 'max', value: string) => {
    if (type === 'min') handleQtyDoneChange('min', value);
    else handleQtyDoneChange('max', value);
  };

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status.length > 0) count++;
    if (filters.type.length > 0) count++;
    if (filters.location_ids.length > 0) count++;
    if (filters.courier.length > 0) count++;
    if (filters.created_at?.from && filters.created_at?.to) count++;
    if (filters.qty.min || filters.qty.max) count++;
    if (filters.qty_done.min || filters.qty_done.max) count++;
    return count;
  }, [filters]);

  return (
    <div className={cn('space-y-5', className)}>
      <FilterSection title={t('filters.basic_info', 'Información básica')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.status')}>
            <MultiSelect
              options={statusOptions}
              value={filters.status}
              onValueChange={handleStatusChange}
              placeholder={t('filters.select_status', 'Seleccionar estado')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-status"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.preparationType')}>
            <MultiSelect
              options={typeOptions}
              value={filters.type}
              onValueChange={handleTypeChange}
              placeholder={t('filters.select_type', 'Seleccionar tipo')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-type"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.cart')}>
            <MultiSelect
              options={cartOptions}
              value={filters.location_ids}
              onValueChange={handleLocationIdsChange}
              placeholder={t('filters.select_cart', 'Seleccionar carrito')}
              emptyText={t('filters.notFound', 'Sin resultados')}
              dataTestId="filter-location-ids"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          {/* Filtro courier - Courier filter */}
          <FilterField label={t('filters.courier', 'Courier')}>
            <MultiSelect
              options={courierOptions}
              value={filters.courier}
              onValueChange={handleCourierChange}
              placeholder={
                isLoadingCouriers
                  ? t('filters.loading_couriers', 'Cargando...')
                  : t('filters.all_couriers', 'Todos los couriers')
              }
              emptyText={t('filters.notFound', 'Sin resultados')}
              disabled={isLoadingCouriers}
              dataTestId="filter-courier"
              triggerClassName="h-[38px]"
            />
          </FilterField>

          <FilterField label={t('filters.date')}>
            <DateRangePicker
              initialDateFrom={filters.created_at?.from}
              initialDateTo={filters.created_at?.to}
              onUpdate={({ range }) => handleCreatedAtChange(range)}
              placeholder={t('filters.date_placeholder', 'Seleccionar rango')}
              dataTestId="filter-created-at"
              triggerClassName="h-[38px] text-sm"
            />
          </FilterField>
        </div>
      </FilterSection>

      <FilterSection title={t('filters.numbers', 'Cantidades')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FilterField label={t('filters.quantity')}>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={localQty.min}
                onChange={(e) => setLocalQty((prev) => ({ ...prev, min: e.target.value }))}
                onBlur={() => applyQty('min', localQty.min)}
                onKeyDown={(e) => e.key === 'Enter' && applyQty('min', localQty.min)}
                placeholder="Min"
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <span className="text-muted-foreground">-</span>
              <input
                type="number"
                value={localQty.max}
                onChange={(e) => setLocalQty((prev) => ({ ...prev, max: e.target.value }))}
                onBlur={() => applyQty('max', localQty.max)}
                onKeyDown={(e) => e.key === 'Enter' && applyQty('max', localQty.max)}
                placeholder="Max"
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
          </FilterField>

          <FilterField label={t('filters.orders')}>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={localQtyDone.min}
                onChange={(e) => setLocalQtyDone((prev) => ({ ...prev, min: e.target.value }))}
                onBlur={() => applyQtyDone('min', localQtyDone.min)}
                onKeyDown={(e) => e.key === 'Enter' && applyQtyDone('min', localQtyDone.min)}
                placeholder="Min"
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
              <span className="text-muted-foreground">-</span>
              <input
                type="number"
                value={localQtyDone.max}
                onChange={(e) => setLocalQtyDone((prev) => ({ ...prev, max: e.target.value }))}
                onBlur={() => applyQtyDone('max', localQtyDone.max)}
                onKeyDown={(e) => e.key === 'Enter' && applyQtyDone('max', localQtyDone.max)}
                placeholder="Max"
                className="flex h-[38px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </div>
          </FilterField>
        </div>
      </FilterSection>

      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-end pt-3 border-t border-card-border">
          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground"
            onClick={clearAllFilters}
            type="button"
          >
            {t('filters.clear', 'Limpiar filtros')} ({activeFiltersCount})
          </Button>
        </div>
      )}
    </div>
  );
};

const FilterSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-3">
    <h4 className="text-xs font-semibold text-subtext">{title}</h4>
    {children}
  </div>
);

const FilterField = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-muted-foreground">{label}</label>
    {children}
  </div>
);
